import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";

interface ColumnFilterProps {
  /** The column heading — also the button that opens the list. */
  label: string;
  /** Every value in the column, with how many rows carry it. */
  options: { value: string; count: number }[];
  /** Ticked values. Empty means no filter — every row shows. */
  selected: string[];
  onChange: (next: string[]) => void;
}

const GAP = 4;
const MARGIN = 8;

/**
 * A table heading that filters its own column.
 *
 * Click the heading and the column's values drop down with a tickbox beside each; tick any number and
 * the table narrows to rows carrying one of them. Nothing ticked is no filter. The heading shows how
 * many values are ticked, so a filtered column is visible at a glance without a separate filter row.
 *
 * The list is position: fixed, anchored to the heading, so the table's own scroll container does not
 * clip it (the same reason the ChatBot query log's header filter is built this way). It closes on a
 * click outside, Escape, or a scroll or resize that would leave it hanging away from its heading —
 * except a scroll inside the list itself.
 */
const ColumnFilter = ({ label, options, selected, onChange }: ColumnFilterProps) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) {
      setPos(null);
      return;
    }
    const anchor = buttonRef.current.getBoundingClientRect();
    const width = listRef.current?.offsetWidth ?? 0;
    const left = Math.max(MARGIN, Math.min(anchor.left, window.innerWidth - width - MARGIN));
    setPos({ top: anchor.bottom + GAP, left });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (buttonRef.current?.contains(target) || listRef.current?.contains(target)) return;
      close();
    };
    const onScroll = (e: Event) => {
      if (listRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  const active = selected.length > 0;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`vr-colfilter${active ? " vr-colfilter-on" : ""}`}
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        title={active ? `Filtered: ${selected.join(", ")}` : `Filter by ${label.toLowerCase()}`}
      >
        {label}
        {active && <span className="vr-colfilter-count">{selected.length}</span>}
        <ChevronDown size={11} />
      </button>
      {/* Portalled to the module root. The table sits in a glass card whose backdrop-filter makes it
          the containing block for anything position: fixed inside it, which would place the list
          relative to the card rather than the screen. The root still carries the theme variables. */}
      {open &&
        createPortal(
        <div
          ref={listRef}
          className="vr-colfilter-list"
          role="group"
          aria-label={`${label} filter`}
          style={pos ? { top: pos.top, left: pos.left } : { top: 0, left: 0, visibility: "hidden" }}
        >
          {options.map((o) => (
            <label key={o.value} className="vr-colfilter-row">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              <span className="vr-colfilter-value">{o.value}</span>
              <span className="vr-colfilter-n">{o.count}</span>
            </label>
          ))}
          {options.length === 0 && <div className="vr-colfilter-empty">Nothing to filter</div>}
          {active && (
            <button type="button" className="vr-colfilter-clear" onClick={() => onChange([])}>
              Clear
            </button>
          )}
        </div>,
          (buttonRef.current?.closest(".vr-root") as HTMLElement | null) ?? document.body,
        )}
    </>
  );
};

export default ColumnFilter;
